'use client'

import { FC, useEffect, useState } from 'react'

import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'
import { useParams, useRouter, useSearchParams } from 'next/navigation'

import { ButtonClick, track } from '@dfds-frontend/tracking'

import LogoWrapper from '@/app/[locale]/[ferry]/_components/navigation/LogoWrapper'
import { Skeleton } from '@/app/[locale]/[ferry]/Skeleton'
import { useBasket } from '@/providers/basket-context'
import { selectedPetsAtom } from '@/states/selectionAtom'

import { getSessionBasket } from '@/utils/sessionStorage'

import { useWindowWidth } from '@/hooks/useWindowWidth'

import { StepType } from './steps'
import { StickySummaryContainer } from './summary/StickySummary'
import { getSteps } from './summary/SummaryUtils'
import { TabletAndMobileStepperHeader } from './TabletAndMobileStepperHeader'

interface Props {
  badgeLetter?: string
  pageStep: StepType
  loginBadgeWrapper: React.ReactNode
}

export const TabletAndMobileStepper: FC<Props> = ({ loginBadgeWrapper, pageStep }) => {
  const { basket, basketId: contextBasketId, residentSwitchLoading } = useBasket()
  const { widthSmallerThan1024 } = useWindowWidth()

  const [showSummary, setShowSummary] = useState<boolean>(false)
  const router = useRouter()
  const params = useParams()
  const locale = params && params.locale ? (Array.isArray(params.locale) ? params.locale[0] : params.locale) : undefined

  const searchParams = useSearchParams()
  // useSearchParams().get() can return null; fall back to the basket context (session-backed)
  // so we never build a `?basketId=null` URL that the middleware would reject.
  const basketId = searchParams.get('basketId') ?? contextBasketId

  const { showNumberOfPets } = useAtomValue(selectedPetsAtom)
  const steps = getSteps(showNumberOfPets)
  const currentStep = steps.find((s) => s.stepType === pageStep) || steps[0]

  const index: number = steps.findIndex((s) => s.stepType == currentStep.stepType)

  const onBackClicked = () => {
    const currentIndex = steps.findIndex((s) => s.stepType === currentStep?.stepType)
    if (currentIndex > 1) {
      let placeToNavigate = undefined
      const stepBefore = steps[currentIndex - 1]
      if (stepBefore.stepType === 'DEPARTURES') placeToNavigate = 'yourbooking'
      else if (stepBefore.stepType === 'PETS') placeToNavigate = 'pets'
      else if (stepBefore.stepType === 'EXTRAS') placeToNavigate = 'extras'
      else if (stepBefore.stepType === 'PASSENGERS') placeToNavigate = 'passengers'
      else if (stepBefore.stepType === 'CHECKOUT') placeToNavigate = 'checkout'
      else if (stepBefore.stepType === 'REVIEW') placeToNavigate = 'review'

      track<ButtonClick>({
        clickedElementText: '',
        event: 'buttonClick',
        eventAction: 'StepperBackClicked',
      })

      if (placeToNavigate) {
        // Prefer the URL/context id, but fall back to sessionStorage so we never navigate to a
        // bare step URL (which the middleware rejects and bounces to marketing) when the basket
        // context has not populated yet.
        let sessionBasketId = ''
        try {
          sessionBasketId = getSessionBasket()?.basketId ?? ''
        } catch {
          // Ignore sessionStorage read/parse errors and fall back to the default empty id.
        }
        const resolvedBasketId = basketId || sessionBasketId
        const basketIdQuery = resolvedBasketId ? `?basketId=${encodeURIComponent(resolvedBasketId)}` : ''
        router.push(`/${params?.locale}/${params?.ferry}/${placeToNavigate}${basketIdQuery}`)
      }
    } else {
      track<ButtonClick>({
        clickedElementText: '',
        event: 'buttonClick',
        eventAction: 'StepperBackClicked',
      })
      router.push(`/${params?.locale}/${params?.ferry}`)
    }
  }

  useEffect(() => {
    if (showSummary && !widthSmallerThan1024) {
      setShowSummary(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthSmallerThan1024])

  return (
    <div
      className={clsx('flex w-full flex-col', showSummary && 'bg-semantic-global-background-white fixed top-0 z-[100] h-full min-h-dvh')}
    >
      {showSummary ? (
        <header className="bg-semantic-global-background-white px-semantic-global-md flex min-h-15.5 w-full items-center justify-between">
          <div className="lg:hidden">
            <LogoWrapper variant="dark" ferry={params?.ferry as string} locale={locale as string} />
          </div>
          <div className="flex items-center lg:hidden">{loginBadgeWrapper}</div>
        </header>
      ) : null}

      <TabletAndMobileStepperHeader
        index={index}
        onBackClicked={onBackClicked}
        pageStep={pageStep}
        setShowSummary={setShowSummary}
        steps={steps}
      />

      {basket && !residentSwitchLoading && showStickySummary(pageStep) ? (
        <StickySummaryContainer
          basket={basket}
          currentIndex={index}
          currentStep={currentStep}
          setShowSummary={setShowSummary}
          showSummary={showSummary}
          steps={steps}
        />
      ) : showStickySummary(pageStep) ? (
        <div className="bg-semantic-global-background-white z-[100] flex h-11 flex-col">
          <div className="px-primitive-16 flex h-full flex-row items-center">
            <Skeleton className="h-primitive-16 w-37.5" />
            <Skeleton className="h-primitive-16 ml-auto w-12.5" />
          </div>
        </div>
      ) : null}
    </div>
  )
}

const showStickySummary = (step: StepType) => (['TRAVEL', 'PENDING', 'CONFIRMATION-FAILED', 'CONFIRMATION'].includes(step) ? false : true)
